/**
 * `/entrar` — telefone e OTP.
 *
 * `verifyOtp` bem-sucedido nao guarda nada: o servidor manda o `Set-Cookie` do
 * `__Host-session`, o navegador o guarda, e o app so atualiza o espelho de sessao.
 *
 * ## `?voltarPara=` — entrar no meio de uma tarefa e voltar para ela
 *
 * O checkout exige sessao (`API §6.12`: `POST /v1/checkout/quote` e `SES`) e manda para ca quem
 * chegou deslogado. Sem o parametro de volta, o OTP jogava a pessoa na Home e ela tinha de
 * refazer o caminho ate o carrinho — que e o momento em que se desiste da compra.
 *
 * O destino e **validado**: so caminho interno (`/algo`). `//outro.site` e `https://…` sao
 * descartados, e a pessoa cai na Home. Um "para onde ir depois de entrar" vindo da URL sem essa
 * checagem e redirecionamento aberto — o vetor classico de phishing em tela de login.
 *
 * A checagem aparece **duas vezes**, e a segunda e a que vale. `validateSearch` declara o tipo
 * do parametro, mas foi medido (`routes/__tests__/checkout-fecha.test.tsx`) que esta versao do
 * roteador entrega ao componente a busca **crua**, sem passar pelo validador — inclusive na
 * `/busca`, que ja usava a mesma forma. Confiar so nele deixaria `?voltarPara=https://…` chegar
 * inteiro ao `navigate`. Por isso `destinoInterno` e chamado de novo no **ponto de uso**, que e
 * onde a garantia nao depende de comportamento de biblioteca.
 *
 * ## O carrinho anonimo e fundido ANTES de sair daqui
 *
 * `API §6.9` da a `POST /v1/cart/merge` a funcao de *"fundir carrinho anonimo ao logar"*, com a
 * uniao por `qty = max(local, remoto)` filtrada por regra regulatoria. O merge acontece dentro
 * da mutacao, antes da navegacao, de proposito: navegar primeiro faria o checkout cotar o
 * carrinho que o merge esta prestes a substituir — e a pessoa veria um total mudar sozinho
 * depois de ver o primeiro.
 */
import { ApiError, type ApiClient } from '@farmacia/api-client';
import { queryKeys, useApiClient } from '@farmacia/api-client/query';
import { toE164Br } from '@farmacia/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';

import { obterDeviceId, rotuloDoDispositivo } from '../app/dispositivo.js';
import { descreverErro } from '../app/erros.js';
import { useSessao } from '../app/sessao.jsx';
import { usePwa } from '../pwa/contexto.jsx';
import { Entrar, type EtapaDeEntrada } from '../telas/Entrar.jsx';

/** Janela de reenvio. O servidor tem a dele (`API §9`); esta so evita o toque inutil. */
const SEGUNDOS_PARA_REENVIAR = 60;

interface EntrarSearch {
  readonly voltarPara?: string;
}

/**
 * Caminho interno, e nada mais.
 *
 * Exige `/` no comeco e **recusa** `//`: `//evil.example` e uma URL protocolo-relativa, que o
 * navegador resolve para outra origem apesar de parecer um caminho. `/\` entra na recusa pelo
 * mesmo motivo — alguns navegadores normalizam a barra invertida para `/`.
 */
export function destinoInterno(bruto: unknown): string | null {
  if (typeof bruto !== 'string') return null;
  if (!bruto.startsWith('/')) return null;
  if (bruto.startsWith('//') || bruto.startsWith('/\\')) return null;
  return bruto;
}

export const Route = createFileRoute('/entrar')({
  validateSearch: (bruto: Record<string, unknown>): EntrarSearch => {
    const destino = destinoInterno(bruto.voltarPara);
    return destino === null ? {} : { voltarPara: destino };
  },
  component: TelaDeEntrada,
});

/**
 * O que a fusao produziu. `CONFLITO_DE_LOJA` e o unico desfecho que muda para onde a pessoa vai.
 */
export type DesfechoDaFusao = 'FUNDIDO' | 'CONFLITO_DE_LOJA';

/**
 * Para onde ir depois do OTP.
 *
 * Funcao pura pelo mesmo motivo de `destinoInterno` logo acima: a decisao inteira cabe numa
 * tabela, e dentro do `onSuccess` de uma mutation ela so seria alcancavel por um teste que
 * dirigisse dois passos de OTP com cliente HTTP falso. Aqui ela e verificavel diretamente.
 *
 * | Desfecho | Destino | Por que |
 * | --- | --- | --- |
 * | `CONFLITO_DE_LOJA` | `/carrinho` | X-52 — e a unica tela que compara as duas listas e refaz o espelho; o destino pedido levaria ao checkout com o carrinho errado |
 * | `FUNDIDO` | `voltarPara`, ou `/` | o caminho normal: a pessoa volta para a tarefa que interrompeu |
 *
 * `destinoInterno` e reaplicado aqui, e nao so no `validateSearch`: ver o cabecalho do arquivo —
 * esta versao do roteador entrega a busca crua ao componente, e confiar no validador deixaria
 * `?voltarPara=https://…` chegar inteiro ao `navigate`.
 */
export function destinoDepoisDeEntrar(fusao: DesfechoDaFusao, voltarPara: unknown): string {
  if (fusao === 'CONFLITO_DE_LOJA') return '/carrinho';
  return destinoInterno(voltarPara) ?? '/';
}

/**
 * Funde o carrinho do visitante ao do titular.
 *
 * ## O `catch` vazio dizia "so esta falha" e engolia TODAS (X-52)
 *
 * O comentario anterior declarava a intencao certa — *"a falha e engolida de proposito, **e so
 * esta**: quem entra sem nunca ter posto nada no carrinho e o caso comum"* — e o `catch {
 * return; }` embaixo dele nao distinguia caso nenhum. `POST /v1/cart/merge` tem exatamente dois
 * desfechos de recusa, e eles nao se parecem:
 *
 * | Codigo | O que aconteceu | O certo |
 * | --- | --- | --- |
 * | `CART_EMPTY` | nao havia nada para fundir — **o caso comum** | engolir; reprovar o login por isso trocaria o caminho normal por um erro |
 * | `CART_STORE_MISMATCH` | o carrinho anonimo e o do titular estao em filiais diferentes | **avisar**, porque o servidor recusou de proposito e quem escolhe e o cliente |
 *
 * Engolindo o segundo, a pessoa entrava, era levada ao checkout e via o carrinho ANTIGO: os itens
 * que ela acabou de montar sumiam sem erro, sem aviso e sem explicacao. E nao era transitorio —
 * o carrinho anonimo continua vivo no cookie por 7 dias, entao todo login seguinte repetia a
 * recusa, e deslogar trazia os itens de volta e sumia com os antigos.
 *
 * ## Por que o carrinho anonimo NAO e aposentado na recusa, e isso esta certo
 *
 * `retireAnonymous` roda depois do ponto em que `#filialUnica` lanca, e manter assim e
 * deliberado: `DOMAIN §4.1` diz que na divergencia *"o servidor nao decide... o cliente
 * escolhe"*, e aposentar o carrinho anonimo destruiria justamente a metade sobre a qual ele
 * precisa escolher. O que faltava nao era aposentar; era **contar**.
 *
 * ## Para onde a pessoa vai
 *
 * Para o carrinho, e nao para o destino que ela pediu. `routes/carrinho.tsx` e a unica tela que
 * ja sabe resolver isto: ela compara as duas listas com `diferencaDoCarrinho` e refaz o espelho
 * com `PUT /v1/cart`, nomeando item por item o que muda. Mandar a pessoa ao checkout com o
 * carrinho errado e o defeito; mandar para a tela que conserta e a saida que ja existe.
 */
export async function fundirCarrinhoAnonimo(cliente: ApiClient): Promise<DesfechoDaFusao> {
  try {
    // O corpo vai vazio: o carrinho anonimo do SERVIDOR e achado pelo cookie `__Host-cart`, e
    // `MergeCartRequestSchema` tem todos os campos opcionais (`contracts/public/cart.ts`).
    await cliente.cart.merge({});
    return 'FUNDIDO';
  } catch (erro: unknown) {
    // **A recusa NAO pode virar erro do login.** A sessao ja foi criada por
    // `verifyOtp` — deixar a excecao subir pela `mutationFn` levaria a mutation para o estado de
    // erro e a tela diria que a entrada falhou, sobre uma entrada que deu certo. O desfecho volta
    // como VALOR, e quem decide o que fazer com ele e o `onSuccess`.
    if (erro instanceof ApiError && erro.code === 'CART_STORE_MISMATCH') return 'CONFLITO_DE_LOJA';
    // `CART_EMPTY` (o caso comum: nao havia o que fundir) e qualquer outra falha — inclusive
    // queda de rede — nao impedem a entrada. O carrinho do titular vive no servidor e continua
    // la; a proxima leitura o traz.
    return 'FUNDIDO';
  }
}

function TelaDeEntrada(): ReactNode {
  const cliente = useApiClient();
  const navegar = useNavigate();
  const queryClient = useQueryClient();
  const { voltarPara } = Route.useSearch();
  const { definirEstado } = useSessao();
  const { conexao } = usePwa();

  const [etapa, setEtapa] = useState<EtapaDeEntrada>('TELEFONE');
  const [telefone, setTelefone] = useState('');

  /**
   * **O que a pessoa digita nao e E.164, e o servidor so aceita E.164.**
   *
   * `PhoneBrE164Schema` exige `+55` seguido de DDD e numero; ninguem digita o codigo do pais.
   * Verificado no navegador: `(88) 99999-7777` e `88999997777` respondiam `422 VALIDATION_ERROR`
   * com *"DDD NaN nao existe"*, e a tela mostrava "Confira os campos destacados" sem dizer o
   * que corrigir — porque nao havia nada errado com o que ela escreveu. So `+5588999997777`
   * funcionava, o que na pratica e "o login nao funciona".
   *
   * `toE164Br` mora em `@farmacia/contracts` (ao lado do schema) exatamente para isto: o
   * mesmo pacote que define a forma tambem oferece a conversao, e nao ha uma segunda
   * normalizacao para divergir. O que o servidor recusa continua sendo recusado — um numero
   * com DDD invalido nao vira valido por passar aqui.
   */
  const telefoneE164 = toE164Br(telefone);
  const [codigo, setCodigo] = useState('');
  const [segundos, setSegundos] = useState(0);

  useEffect(() => {
    if (segundos <= 0) return undefined;
    const id = setTimeout(() => {
      setSegundos((s) => s - 1);
    }, 1000);
    return () => {
      clearTimeout(id);
    };
  }, [segundos]);

  const pedirCodigo = useMutation({
    mutationFn: () => cliente.auth.requestOtp({ phone: telefoneE164 }),
    onSuccess: () => {
      setEtapa('CODIGO');
      setSegundos(SEGUNDOS_PARA_REENVIAR);
    },
  });

  const confirmar = useMutation({
    mutationFn: async () => {
      const sessao = await cliente.auth.verifyOtp({
        phone: telefoneE164,
        code: codigo,
        // O contrato exige: UUID do APARELHO, persistido localmente, que sobrevive ao logout.
        // Ver `app/dispositivo.ts` para por que ele nao entra na purga.
        deviceId: obterDeviceId(),
        deviceLabel: rotuloDoDispositivo(),
      });
      const fusao = await fundirCarrinhoAnonimo(cliente);
      return { sessao, fusao };
    },
    onSuccess: ({ fusao }) => {
      definirEstado('AUTENTICADA');
      // O carrinho em cache e o do visitante. Sem invalidar, a tela de volta desenharia a lista
      // de antes do merge e cotaria em cima dela.
      void queryClient.invalidateQueries({ queryKey: queryKeys.cart.current() });

      // A decisao inteira (inclusive o X-52 e a revalidacao do destino externo) mora na funcao
      // pura acima, que tem teste proprio.
      void navegar({ to: destinoDepoisDeEntrar(fusao, voltarPara) });
    },
  });

  /*
   * `erro.message` de um `ApiError` e `"VALIDATION_ERROR (422) em POST /v1/auth/otp/verify"` —
   * string de log, que estava sendo escrita DENTRO do campo do formulario. DESIGN_SYSTEM §7.11:
   * *"Nunca exibir stack trace, 'Error 500' nem o nome do gateway"*. O que a pessoa le agora e a
   * frase que o catalogo redigiu para aquele codigo.
   */
  const erro = descreverErro(confirmar.error ?? pedirCodigo.error);

  return (
    <Entrar
      etapa={etapa}
      telefone={telefone}
      aoMudarTelefone={setTelefone}
      codigo={codigo}
      aoMudarCodigo={setCodigo}
      aoEnviarCodigo={() => {
        pedirCodigo.mutate();
      }}
      aoConfirmar={() => {
        confirmar.mutate();
      }}
      aoTrocarTelefone={() => {
        setEtapa('TELEFONE');
        setCodigo('');
      }}
      aoReenviar={() => {
        pedirCodigo.mutate();
      }}
      segundosParaReenviar={segundos}
      enviando={pedirCodigo.isPending || confirmar.isPending}
      offline={conexao === 'OFFLINE'}
      {...(erro === null ? {} : { erro: erro.mensagem })}
    />
  );
}
